import { isPrimaryBillingContact } from 'CustomerContacts/utils/contactPurposes'
import { useMemo, useState } from 'react'

import { Button } from '@sequencehq/core-components'

import { ManualTaskLayout } from 'Automations/components'
import { useCustomerInfoReviewTask } from '../../hooks/useCustomerInfoReviewTask'
import {
  applyFormDataToCustomer,
  customerSnapshotToFormData,
  getChangedContactIds,
  restoreUnchangedContacts
} from '../../customerInfoDiff'
import {
  CustomerInfoReviewOutcome,
  CustomerInfoReviewViewModel,
  CustomerSnapshot
} from '../../types'
import { CustomerInfoReviewHeader } from './CustomerInfoReviewHeader'
import { CustomerChangeEditorPanel } from './CustomerChangeEditorPanel'
import { CustomerChangeReview } from './CustomerChangeReview'
import { ModalCustomerInfoReviewResponse } from './CustomerInfoReviewResponse.modal'
import {
  getActiveReviewSteps,
  getConfirmActionLabel,
  REVIEW_STEP_TITLES
} from './steps'

export const CustomerInfoReviewContent = ({ taskId }: { taskId: string }) => {
  const { viewModel, isPending, handleClose, completeTask } =
    useCustomerInfoReviewTask({ taskId })

  if (isPending) {
    return <ManualTaskLayout.Loading />
  }

  if (!viewModel) {
    return <ManualTaskLayout.Error />
  }

  return (
    <ReviewContent
      taskId={taskId}
      viewModel={viewModel}
      handleClose={handleClose}
      completeTask={completeTask}
    />
  )
}

const ReviewContent = ({
  taskId,
  viewModel,
  handleClose,
  completeTask
}: {
  taskId: string
  viewModel: CustomerInfoReviewViewModel
  handleClose: () => void
  completeTask: (outcome: CustomerInfoReviewOutcome) => Promise<boolean>
}) => {
  // The contacts a change touched (added or modified). The review only surfaces
  // these — an unchanged contact that merely rode along in the proposal isn't
  // shown or editable — but they're all restored on confirm (see below).
  const changedContactIds = useMemo(
    () => getChangedContactIds(viewModel.before, viewModel.after),
    [viewModel.before, viewModel.after]
  )

  // The proposal the form and preview work off: the agent's snapshot with its
  // contacts narrowed to the changed ones. Non-contact fields (legal name,
  // address) are untouched.
  const proposedForReview = useMemo(
    () => ({
      ...viewModel.after,
      contacts: (viewModel.after.contacts ?? []).filter(contact =>
        changedContactIds.has(contact.id)
      )
    }),
    [viewModel.after, changedContactIds]
  )

  // The contacts of the original record the form never shows: everything the
  // change didn't touch, including any contact the proposal tried to remove
  // (removals aren't supported through this flow, so they're never applied
  // blind). All of them are restored as-is on confirm (see `submittedCustomer`).
  const restoredContacts = useMemo(
    () =>
      (viewModel.before.contacts ?? []).filter(
        contact => !changedContactIds.has(contact.id)
      ),
    [viewModel.before, changedContactIds]
  )

  // Whether the roster's PRIMARY contact sits among the restored contacts. The
  // form must not then require — or silently auto-promote — a primary among the
  // contacts it shows: the restored contact keeps the role, and promoting a
  // shown one would submit a record with two PRIMARY contacts.
  const primaryContactIsRestored = restoredContacts.some(
    isPrimaryBillingContact
  )

  // The working draft both steps edit: the original record with the agent's
  // reviewable proposed edits (legal name, billing address, changed contacts)
  // applied on top. Its contacts are the changed subset so the form and preview
  // stay in lockstep (the focus ring maps by index); the unchanged contacts are
  // restored on confirm. Seeded through the same transform the form uses so that
  // — even if the operator confirms without touching a field — website/domain/
  // alias/tax status the review can't surface stay as they were on the original
  // record rather than the agent's unreviewed proposal.
  const [editedCustomer, setEditedCustomer] = useState<CustomerSnapshot>(() =>
    applyFormDataToCustomer(
      viewModel.before,
      customerSnapshotToFormData(proposedForReview)
    )
  )

  // The record submitted on confirm: the working draft with the restored
  // contacts (which the form never showed) carried through from the original
  // record, so nothing is silently dropped even though only the changed
  // contacts were reviewed.
  const submittedCustomer = useMemo<CustomerSnapshot>(
    () => ({
      ...editedCustomer,
      contacts: restoreUnchangedContacts(
        viewModel.before,
        editedCustomer,
        changedContactIds
      )
    }),
    [editedCustomer, viewModel.before, changedContactIds]
  )
  // Validity is owned by the shared form (via onValidationStateChange); the
  // proposal is assumed valid until the form reports otherwise.
  const [isValid, setIsValid] = useState(true)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  // Only the steps that actually changed (derived from the diff), so a task that
  // touches just the billing address or just the contacts shows a single step
  // and no stepper; a task touching both shows the full two-step flow.
  const steps = getActiveReviewSteps(viewModel.sections)
  const confirmActionLabel = getConfirmActionLabel(steps)
  const [stepIndex, setStepIndex] = useState(0)
  const step = steps[stepIndex]
  const isFirstStep = stepIndex === 0
  const isFinalStep = stepIndex === steps.length - 1

  // Which contact form is focused on the contacts step, so the preview can ring
  // the matching card. The editor reports the focused contact's index; it stays
  // put when focus moves off the contact fields, so the preview keeps showing
  // the last focused contact rather than blanking. `null` before the first focus
  // (on entering the step the editor autofocuses the first contact, setting this
  // to 0) — treated as the first contact so a card always shows.
  const [activeContactIndex, setActiveContactIndex] = useState<number | null>(
    null
  )
  // Default to the first contact when none has been focused yet, so the preview
  // always shows a contact card. Guard the index→id mapping against a stale
  // index too: removing a contact shrinks the live snapshot without emitting a
  // focus/mouse event to update `activeContactIndex`, so an index past the new
  // end would otherwise resolve to `undefined`. Clamp to the last remaining
  // contact.
  const editedContacts = editedCustomer.contacts ?? []
  const activeContactId =
    editedContacts.length === 0
      ? undefined
      : (
          editedContacts[activeContactIndex ?? 0] ??
          editedContacts[editedContacts.length - 1]
        )?.id

  const goToStep = (index: number) => {
    setStepIndex(index)
    // Each step starts without surfaced errors; they appear if the operator
    // tries to advance/confirm an invalid form.
    setShowValidationErrors(false)
    // Reset to the default (first contact); the next step's autofocus re-sets it
    // if it lands on a specific contact field.
    setActiveContactIndex(null)
  }

  const handleAdvance = () => {
    if (!isValid) {
      setShowValidationErrors(true)
      return
    }
    goToStep(stepIndex + 1)
  }

  const actions = (
    <ManualTaskLayout.Actions>
      {/* Back is only relevant to the multi-step flow, so it's hidden
          when there's no stepper and disabled on the first step rather
          than appearing/disappearing as the operator advances. Dismiss
          lives outside this modal (the task actions menu). */}
      {steps.length > 1 && (
        <Button
          variant="secondary"
          disabled={isFirstStep}
          onClick={() => goToStep(stepIndex - 1)}
          data-testid="customerInfoReview.back"
        >
          Back
        </Button>
      )}
      {isFinalStep ? (
        <ConfirmAction
          isValid={isValid}
          label={confirmActionLabel}
          customerName={viewModel.customer.legalName}
          editedCustomer={submittedCustomer}
          completeTask={completeTask}
          onInvalid={() => setShowValidationErrors(true)}
        />
      ) : (
        <Button
          variant="primary"
          onClick={handleAdvance}
          data-testid="customerInfoReview.continue"
        >
          Next
        </Button>
      )}
    </ManualTaskLayout.Actions>
  )

  return (
    <ManualTaskLayout
      data-testid={`customerInfoReview.${taskId}`}
      actions={actions}
      header={
        <ManualTaskLayout.Header
          taskId={taskId}
          title={
            <CustomerInfoReviewHeader
              customer={viewModel.customer}
              stepTitle={REVIEW_STEP_TITLES[step]}
            />
          }
          onClose={handleClose}
        />
      }
      content={
        // Preview on the left (flexes to fill), editable form on the right
        // (fixed width) — the `1fr auto` convention shared with the contract
        // intake review.
        <ManualTaskLayout.Content
          // On mobile keep the editable form and drop the comparison preview.
          mobilePrimary="review"
          subject={
            <CustomerChangeReview
              step={step}
              before={viewModel.before}
              // The comparison previews what will actually be submitted — the
              // edited contacts plus the restored ones — so a restored contact
              // (unchanged, or a proposal removal this flow never applies)
              // reads as unchanged rather than as removed.
              after={submittedCustomer}
              source={viewModel.source}
              activeContactId={activeContactId}
            />
          }
          review={
            <CustomerChangeEditorPanel
              step={step}
              originalCustomer={viewModel.before}
              proposedCustomer={proposedForReview}
              onCustomerChange={setEditedCustomer}
              onValidationStateChange={setIsValid}
              onActiveContactIndexChange={setActiveContactIndex}
              activeContactIndex={activeContactIndex}
              showValidationErrors={showValidationErrors}
              requirePrimaryContact={!primaryContactIsRestored}
              requireContacts={restoredContacts.length === 0}
            />
          }
        />
      }
    />
  )
}

/**
 * Final-step primary action. When the form is valid it opens the confirmation
 * dialog; otherwise it surfaces the field errors instead of confirming.
 */
const ConfirmAction = ({
  isValid,
  label,
  customerName,
  editedCustomer,
  completeTask,
  onInvalid
}: {
  isValid: boolean
  label: string
  customerName: string
  editedCustomer: CustomerSnapshot
  completeTask: (outcome: CustomerInfoReviewOutcome) => Promise<boolean>
  onInvalid: () => void
}) => {
  if (!isValid) {
    return (
      <Button
        variant="primary"
        onClick={onInvalid}
        data-testid="customerInfoReview.confirm"
      >
        {label}
      </Button>
    )
  }

  return (
    <ModalCustomerInfoReviewResponse
      trigger={
        <Button variant="primary" data-testid="customerInfoReview.confirm">
          {label}
        </Button>
      }
      outcome={{ outcome: 'CONFIRMED', customer: editedCustomer }}
      customerName={customerName}
      completeTask={completeTask}
    />
  )
}
